import { useEffect, useState } from "react";
import { Loader2, ChevronLeft, ChevronRight, KeyRound, Check } from "lucide-react";
import parentApi from "@/lib/parentApi";
import { toast } from "sonner";
import { LOG_TYPES, EVENT_TYPES, describe, fmtTime, photoUrl } from "@/components/daycare/logTypes";
import { IncidentSignCard } from "./IncidentSignCard";

const shift = (iso, days) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };

/** Parent view: live daily timeline (meals, naps, diapers, photos…) + family kiosk PIN. */
export function StudentToday({ studentId }) {
  const [date, setDate] = useState("");
  const [data, setData] = useState(null);
  const token = localStorage.getItem("vlog_parent_token");

  useEffect(() => {
    setData(null);
    parentApi.get(`/parent/students/${studentId}/daily-log`, { params: date ? { date } : {} }).then((r) => { setData(r.data); if (!date) setDate(r.data.date); }).catch(() => setData({ events: [], items: [], summary: {} }));
  }, [studentId, date]);

  if (!data) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>;
  const timeline = [
    ...data.events.map((e) => ({ kind: "event", key: e.occurred_at + e.type, ...e })),
    ...data.items.map((i) => ({ kind: "log", key: i.id, ...i })),
  ].sort((a, b) => new Date(a.occurred_at) - new Date(b.occurred_at));
  const s = data.summary || {};
  const chips = [s.meals && `${s.meals} meal${s.meals === 1 ? "" : "s"}`, s.nap_minutes && `${s.nap_minutes} min nap`, s.diapers && `${s.diapers} diaper/potty`, s.activities && `${s.activities} activit${s.activities === 1 ? "y" : "ies"}`, s.mood && `Mood: ${s.mood}`].filter(Boolean);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4" data-testid="student-today">
      <IncidentSignCard studentId={studentId} />
      <div className="flex items-center justify-between">
        <button onClick={() => setDate(shift(data.date, -1))} className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="today-prev"><ChevronLeft className="h-4 w-4" /></button>
        <div className="text-sm font-semibold text-slate-900">{new Date(`${data.date}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
        <button onClick={() => setDate(shift(data.date, 1))} disabled={data.date >= today} className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-30" data-testid="today-next"><ChevronRight className="h-4 w-4" /></button>
      </div>
      {chips.length > 0 && <div className="flex flex-wrap gap-1.5" data-testid="today-summary">{chips.map((c) => <span key={c} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 capitalize">{c}</span>)}</div>}
      {timeline.length === 0 && <div className="rounded-2xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-400" data-testid="today-empty">Nothing recorded for this day yet.</div>}
      <ol className="relative space-y-3 border-l-2 border-slate-100 pl-5">
        {timeline.map((t) => {
          const m = t.kind === "event" ? (EVENT_TYPES[t.type] || EVENT_TYPES.drop_off) : (LOG_TYPES[t.type] || LOG_TYPES.note);
          return (
            <li key={t.key} className="relative" data-testid={`today-item-${t.key}`}>
              <span className={`absolute -left-[31px] top-1 h-6 w-6 rounded-full border inline-flex items-center justify-center bg-white ${m.cls}`}><m.Icon className="h-3 w-3" /></span>
              <div className="rounded-xl border border-slate-100 bg-white p-3">
                <div className="flex items-center justify-between text-xs text-slate-500"><span className="font-semibold text-slate-900">{m.label}</span><span>{fmtTime(t.occurred_at)}</span></div>
                {t.kind === "event" ? <div className="text-sm text-slate-700">by {t.by}</div> : (
                  <>
                    {(describe(t) || t.note) && <div className="text-sm text-slate-700">{describe(t)}{t.note && <span className="text-slate-500"> — {t.note}</span>}</div>}
                    {t.photo_id && <img src={photoUrl(t.photo_id, token)} alt="" className="mt-2 w-full max-h-72 rounded-xl object-cover" />}
                    {t.recorded_by_name && <div className="mt-1 text-[11px] text-slate-400">— {t.recorded_by_name}</div>}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      <FamilyPinCard />
    </div>
  );
}

function FamilyPinCard() {
  const [pin, setPin] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { parentApi.get("/parent/kiosk-pin").then((r) => setSaved(r.data.pin || "")).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await parentApi.put("/parent/kiosk-pin", { pin }); setSaved(pin); setPin(""); toast.success("Family PIN saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save PIN"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="family-pin-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><KeyRound className="h-4 w-4 text-primary" /> Family kiosk PIN</div>
      <p className="mt-1 text-xs text-slate-500">Use this 4-digit PIN at the front-desk kiosk to sign your children in and out.{saved ? ` Current PIN: ${saved}` : " You haven't set one yet."}</p>
      <div className="mt-2 flex gap-2">
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} inputMode="numeric" placeholder="New 4–8 digit PIN" data-testid="family-pin-input"
          className="h-10 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm tracking-widest focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
        <button onClick={save} disabled={busy || pin.length < 4} className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-medium text-white disabled:opacity-50" data-testid="family-pin-save"><Check className="h-4 w-4" /> Save</button>
      </div>
    </div>
  );
}
